import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiActivity, FiTarget, FiClock, FiDollarSign, FiAward, FiShield, FiCheckCircle, FiBone, FiPhone, FiCalendar, FiChevronDown, FiPlay, FiX, FiStar, FiTrendingUp, FiSearch, FiUsers, FiHeart, FiSmile } = FiIcons;

const ElbowPain = () => {
  const [activeFaq, setActiveFaq] = useState(null);
  const [videoModalOpen, setVideoModalOpen] = useState(false);

  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  const benefits = [
    { icon: FiHeart, title: "Targeted Pain Relief", description: "Effective treatments to resolve pain on the inside or outside of your elbow, restoring comfort." },
    { icon: FiActivity, title: "Return to Sports & Activities", description: "Our goal is to get you back to your favorite sports, hobbies, and daily tasks without pain." },
    { icon: FiTarget, title: "Restored Grip Strength", description: "Treatments focus on strengthening forearm muscles to improve your ability to grip and lift objects." },
    { icon: FiShield, title: "Prevention of Recurrence", description: "We provide guidance on proper form and technique to prevent the injury from coming back." }
  ];

  const causes = [
    { title: "Tennis Elbow (Lateral Epicondylitis)", description: "Pain on the outside of the elbow, caused by overuse of the muscles and tendons of the forearm.", treatment: "Rest, physiotherapy, bracing, and injections are common. Surgery is rare but effective for persistent cases." },
    { title: "Golfer's Elbow (Medial Epicondylitis)", description: "Pain on the inside of the elbow, similar to tennis elbow but affecting the inner tendons.", treatment: "Managed with rest, stretching, strengthening exercises, and sometimes corticosteroid injections." },
    { title: "Olecranon Bursitis", description: "Swelling and inflammation of the bursa at the tip of the elbow, often caused by trauma or prolonged pressure.", treatment: "Often resolves with rest and anti-inflammatory medication. Aspiration or surgery may be needed if it becomes infected or chronic." },
    { title: "Cubital Tunnel Syndrome", description: "Pressure or stretching of the ulnar nerve (the 'funny bone'), causing numbness or tingling in the ring and small fingers.", treatment: "Bracing and therapy can help. Surgery may be needed to relieve pressure on the nerve." }
  ];

  const treatments = [
    { icon: FiActivity, title: "Physiotherapy", description: "Specific exercises to stretch and strengthen the forearm muscles, improving resilience and reducing strain on the elbow tendons." },
    { icon: FiAward, title: "Platelet-Rich Plasma (PRP) Injections", description: "Using your own blood platelets to stimulate and accelerate the natural healing process in the damaged tendon." },
    { icon: FiTarget, title: "Extracorporeal Shock Wave Therapy (ESWT)", description: "A non-invasive treatment that uses sound waves to promote healing in the affected tendon." },
    { icon: FiBone, title: "Minimally Invasive Surgery", description: "For chronic cases that don't respond to conservative care, a surgeon can remove the damaged tissue and repair the tendon." }
  ];

  const processSteps = [
    { icon: FiSearch, title: "Accurate Diagnosis", description: "A thorough physical examination, sometimes supplemented with ultrasound or MRI, to pinpoint the exact cause of your elbow pain." },
    { icon: FiUsers, title: "Conservative Treatment First", description: "We prioritize non-invasive treatments like physiotherapy, bracing, and activity modification to promote healing." },
    { icon: FiHeart, title: "Advanced Interventions", description: "If needed, we offer advanced treatments like PRP injections or shock wave therapy to accelerate recovery." },
    { icon: FiTrendingUp, title: "Guided Return to Activity", description: "Our team provides a structured plan to help you safely return to sports and daily activities without re-injury." }
  ];

  const beforeAfter = [
    { title: "Before Treatment", points: ["Persistent pain in the elbow", "Weak grip strength", "Difficulty with lifting or twisting motions", "Inability to play sports like tennis or golf"] },
    { title: "After Treatment", points: ["Significant pain reduction", "Restored strength and function", "Ability to perform daily tasks without pain", "Safe return to sports and hobbies"] }
  ];

  const testimonials = [
    {
      name: "Rahman Khan",
      country: "Bangladesh",
      flag: "🇧🇩",
      procedure: "Tennis Elbow Treatment",
      thumbnail: "https://img.youtube.com/vi/BRkpeP4Sxlw/maxresdefault.jpg",
      videoUrl: "https://www.youtube.com/watch?v=BRkpeP4Sxlw",
      youtubeId: "BRkpeP4Sxlw",
      quote: "My tennis elbow was preventing me from playing. The treatment in India was fantastic, and I was back on the court in no time!",
      rating: 5
    }
  ];

  const faqs = [
    { question: "What is the difference between Tennis Elbow and Golfer's Elbow?", answer: "Tennis Elbow causes pain on the outside of the elbow, while Golfer's Elbow causes pain on the inside. Both are forms of tendonitis caused by overuse of the forearm muscles, but they affect different tendons." },
    { question: "Do I need surgery for my elbow pain?", answer: "Most cases of elbow pain, including Tennis and Golfer's Elbow, can be successfully treated with non-surgical methods like rest, physical therapy, and injections. Surgery is typically only considered if symptoms persist for 6-12 months despite conservative treatment." },
    { question: "What is PRP therapy?", answer: "Platelet-Rich Plasma (PRP) therapy is a regenerative treatment that uses a concentration of a patient's own platelets to accelerate the healing of injured tendons, ligaments, muscles, and joints. It's a promising treatment for chronic tendon injuries like tennis elbow." },
    { question: "How can I prevent elbow pain from returning?", answer: "Prevention involves proper warm-ups, strengthening forearm muscles, using correct technique in sports and activities, using appropriately sized equipment (like a tennis racket), and taking regular breaks to avoid overuse." }
  ];

  return (
    <motion.main
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="pt-20"
    >
      <Helmet>
        <title>Elbow Pain Treatment in India | Impact Ortho Centre</title>
        <meta name='description' content='Seeking effective elbow pain treatment in India? Impact Ortho Centre offers expert orthopedic care and personalized treatment for lasting pain relief and mobility.' />
      </Helmet>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 to-cyan-50 py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center px-4 py-2 bg-blue-100 text-blue-800 rounded-full text-sm font-medium mb-6">
                <SafeIcon icon={FiTarget} className="w-4 h-4 mr-2" />
                Specialized Upper Limb Care
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Elbow Pain Treatment <span className="bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">in India</span>
              </h1>
              <p className="text-xl text-gray-700 mb-8 leading-relaxed">
                Find expert care for elbow conditions like Tennis Elbow and Golfer's Elbow in India. Our specialists offer advanced non-surgical and minimally invasive treatments to get you back in the game.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-cyan-700 transition-all duration-300 transform hover:scale-105 shadow-lg"
                >
                  <SafeIcon icon={FiCalendar} className="w-5 h-5 mr-2" />
                  Book Free Consultation
                </Link>
                <button
                  onClick={() => window.location.href = 'tel:+919494559848'}
                  className="inline-flex items-center justify-center px-8 py-4 border-2 border-blue-600 text-blue-600 font-semibold rounded-xl hover:bg-blue-600 hover:text-white transition-all duration-300"
                >
                  <SafeIcon icon={FiPhone} className="w-5 h-5 mr-2" />
                  Call Now
                </button>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <img
                src="https://impactorthocenter.com/photos/Elbow-Pain-Treatment.webp"
                alt="Elbow Pain Treatment in India"
                className="w-full h-auto object-cover rounded-2xl shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-10 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Benefits of Our Elbow Pain Treatment
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our comprehensive care model focuses on rapid pain relief and long-term recovery.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <SafeIcon icon={benefit.icon} className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Common Causes Section */}
      <section className="py-10 bg-gradient-to-br from-blue-50 via-cyan-50 to-indigo-50 text-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Common Causes of Elbow Pain</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We diagnose and treat a wide range of conditions affecting the elbow joint and surrounding tendons.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {causes.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
              >
                <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm mb-4">{item.description}</p>
                <div className="bg-blue-50 border-l-4 border-blue-500 text-blue-800 p-3 text-sm rounded-r-lg">
                  <p className="font-semibold">Treatment Approach:</p>
                  <p>{item.treatment}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Treatment Options Section */}
      <section className="py-10 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Advanced Elbow Pain Treatment Options</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We offer the latest conservative and surgical treatments to ensure the best possible outcome for you.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {treatments.map((treatment, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="flex items-start space-x-6"
              >
                <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <SafeIcon icon={treatment.icon} className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{treatment.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{treatment.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-10 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our Elbow Pain Treatment Journey
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A clear, conservative-first approach to getting you back to your best.
            </p>
          </motion.div>
          <div className="relative max-w-3xl mx-auto">
            <div className="absolute left-8 h-full w-0.5 bg-blue-200 top-0 lg:left-1/2 lg:-translate-x-1/2"></div>
            <div className="space-y-12">
              {processSteps.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  viewport={{ once: true }}
                  className="relative flex items-center"
                >
                  <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-full flex items-center justify-center text-white text-2xl font-bold border-4 border-gray-50 z-10 lg:hidden">
                    {index + 1}
                  </div>
                  <div className={`hidden lg:flex w-1/2 ${index % 2 === 0 ? 'justify-end pr-8' : ''}`}>
                    {index % 2 === 0 && (
                      <div className="p-6 bg-white rounded-2xl shadow-lg border border-gray-100 w-full">
                        <div className="flex items-center space-x-4 mb-4 justify-end">
                          <h3 className="text-xl font-bold text-gray-900">{step.title}</h3>
                          <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center">
                            <SafeIcon icon={step.icon} className="w-6 h-6 text-white" />
                          </div>
                        </div>
                        <p className="text-gray-600 text-right">{step.description}</p>
                      </div>
                    )}
                  </div>
                  <div className="hidden lg:flex flex-shrink-0 w-16 h-16 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-full items-center justify-center text-white text-2xl font-bold border-4 border-gray-50 z-10">
                    {index + 1}
                  </div>
                  <div className={`w-full lg:w-1/2 ${index % 2 === 0 ? 'pl-8' : 'pr-8'}`}>
                    <div className="lg:hidden pl-8">
                      <div className="p-6 bg-white rounded-2xl shadow-lg border border-gray-100">
                        <div className="flex items-center space-x-4 mb-4">
                          <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center">
                            <SafeIcon icon={step.icon} className="w-6 h-6 text-white" />
                          </div>
                          <h3 className="text-xl font-bold text-gray-900">{step.title}</h3>
                        </div>
                        <p className="text-gray-600">{step.description}</p>
                      </div>
                    </div>
                    {index % 2 !== 0 && (
                      <div className="hidden lg:block p-6 bg-white rounded-2xl shadow-lg border border-gray-100 w-full ml-8">
                        <div className="flex items-center space-x-4 mb-4">
                          <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center">
                            <SafeIcon icon={step.icon} className="w-6 h-6 text-white" />
                          </div>
                          <h3 className="text-xl font-bold text-gray-900">{step.title}</h3>
                        </div>
                        <p className="text-gray-600">{step.description}</p>
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Before and After Section */}
      <section className="py-10 bg-gradient-to-br from-blue-50 to-cyan-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Life Transformation: Before and After Elbow Pain Treatment
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {beforeAfter.map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
                className={`bg-white rounded-2xl p-8 shadow-lg ${index === 0 ? 'border-l-4 border-red-500' : 'border-l-4 border-green-500'}`}
              >
                <h3 className={`text-2xl font-bold mb-6 ${index === 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {section.title}
                </h3>
                <ul className="space-y-4">
                  {section.points.map((point, pointIndex) => (
                    <li key={pointIndex} className="flex items-start">
                      <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center mt-1 ${index === 0 ? 'bg-red-100' : 'bg-green-100'}`}>
                        <SafeIcon
                          icon={index === 0 ? FiX : FiCheckCircle}
                          className={`w-4 h-4 ${index === 0 ? 'text-red-600' : 'text-green-600'}`}
                        />
                      </div>
                      <span className="ml-3 text-gray-700">{point}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-10 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Your questions about elbow pain treatment, answered.
            </p>
          </motion.div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="border border-gray-200 rounded-xl overflow-hidden"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full flex justify-between items-center bg-white p-6 text-left"
                >
                  <h3 className="text-lg font-semibold text-gray-900">{faq.question}</h3>
                  <SafeIcon
                    icon={FiChevronDown}
                    className={`w-5 h-5 text-gray-500 transition-transform duration-300 ${activeFaq === index ? 'rotate-180' : ''}`}
                  />
                </button>
                {activeFaq === index && (
                  <div className="p-6 bg-gray-50 border-t border-gray-200">
                    <p className="text-gray-600">{faq.answer}</p>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-10 bg-gradient-to-r from-blue-600 to-cyan-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold mb-6">
              Get Expert Elbow Pain Treatment in India
            </h2>
            <p className="text-xl mb-8 text-blue-100 max-w-3xl mx-auto">
              Don't let elbow pain affect your game or your life. Contact our specialists for a personalized evaluation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link

                to="/contact"
                className="inline-flex items-center px-8 py-4 bg-white text-blue-600 font-semibold rounded-xl hover:bg-blue-50 transition-all duration-300 transform hover:scale-105 shadow-xl"
              >
                <SafeIcon icon={FiCalendar} className="w-5 h-5 mr-2" />
                Schedule Consultation
              </Link>
              <button
                onClick={() => window.location.href = 'tel:+919494559848'}
                className="inline-flex items-center px-8 py-4 border-2 border-white text-white font-semibold rounded-xl hover:bg-white hover:text-blue-600 transition-all duration-300"
              >
                <SafeIcon icon={FiPhone} className="w-5 h-5 mr-2" />
                Call Now
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    </motion.main>
  );
};

export default ElbowPain;